import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Snackbar,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import {
  fetchAreasByCityId,
  fetchCities,
  updateCustomer,
} from "redux/actions/customerMaster";
import { CustomerMasterData } from "types/models/customerMaster/CustomerMasterApp";
import { CityModel } from "types/models/CityModel";
import { AreaModel } from "types/models/AreaModel";

interface CustomerProps {
  Customer?: CustomerMasterData;
}

const CustomerUpdate: React.FC<CustomerProps> = ({ Customer }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [cities, setCities] = useState<CityModel[]>([]);
  const [areas, setAreas] = useState<AreaModel[]>([]);

  useEffect(() => {
    dispatch<any>(fetchCities()).then((res: any) => {
      const rows = res?.data?.data || [];
      setCities(Array.isArray(rows) ? rows : []);
    });

    if (Customer?.city) {
      dispatch<any>(fetchAreasByCityId(Number(Customer.city))).then(
        (res: any) => {
          setAreas(Array.isArray(res) ? res : []);
        }
      );
    }
  }, [dispatch, Customer?.city]);

  const handleCityChange = (cityId: string, setFieldValue: any) => {
    setFieldValue("city", cityId);
    setFieldValue("area", "");
    setAreas([]);
    dispatch<any>(fetchAreasByCityId(Number(cityId))).then((res: any) => {
      setAreas(Array.isArray(res) ? res : []);
    });
  };

  const validationSchema = Yup.object({
    contact_name: Yup.string().required("Contact name is required"),
    contact_number: Yup.string()
      .required("Mobile Number is required")
      .matches(/^[0-9]{10}$/, "Mobile Number must be 10 digits"),
    contact_email: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string(),
    address: Yup.string(),
    reference: Yup.string(),
    remarks_at_the_time_of_generation: Yup.string(),
  });

  const handleSubmit = async (values: CustomerMasterData) => {
    try {
      const response = (await dispatch(
        updateCustomer(String(values.PageID), values)
      )) as any;

      if (response?.status === 200) {
        navigate("/customerCare/list");
      } else {
        setErrorSnackbar(true);
      }
    } catch (err) {
      console.error("Update error:", err);
      setErrorSnackbar(true);
    }
  };

  return (
    <Box sx={{ height: "100%", width: "100%", p: 8 }}>
      <Formik
        initialValues={{
          PageID: Customer?.PageID || "",
          contact_name: Customer?.contact_name || "",
          contact_number: Customer?.contact_number || "",
          contact_email: Customer?.contact_email || "",
          city: Customer?.city || "",
          area: Customer?.area || "",
          address: Customer?.address || "",
          reference: Customer?.reference || "",
          lead_source: Customer?.lead_source || "",
          remarks_at_the_time_of_generation:
            Customer?.remarks_at_the_time_of_generation || "",
          state: Customer?.state || "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="contact_name"
                  label="Customer Name"
                  error={touched.contact_name && Boolean(errors.contact_name)}
                  helperText={<ErrorMessage name="contact_name" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="contact_number"
                  label="Mobile Number"
                  error={touched.contact_number && Boolean(errors.contact_number)}
                  helperText={<ErrorMessage name="contact_number" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="contact_email"
                  label="Email ID"
                  error={touched.contact_email && Boolean(errors.contact_email)}
                  helperText={<ErrorMessage name="contact_email" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="remarks_at_the_time_of_generation"
                  label="Remarks"
                  error={
                    touched.remarks_at_the_time_of_generation &&
                    Boolean(errors.remarks_at_the_time_of_generation)
                  }
                  helperText={
                    <ErrorMessage name="remarks_at_the_time_of_generation" />
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="city"
                  label="City"
                  error={touched.city && Boolean(errors.city)}
                  helperText={<ErrorMessage name="city" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="area"
                  label="Area"
                  error={touched.area && Boolean(errors.area)}
                  helperText={<ErrorMessage name="area" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="reference"
                  label="Reference"
                  error={touched.reference && Boolean(errors.reference)}
                  helperText={<ErrorMessage name="reference" />}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  as={TextField}
                  fullWidth
                  name="state"
                  label="State"
                  error={touched.state && Boolean(errors.state)}
                  helperText={<ErrorMessage name="state" />}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  fullWidth
                  multiline
                  minRows={2}
                  name="address"
                  label="Address"
                  error={touched.address && Boolean(errors.address)}
                  helperText={<ErrorMessage name="address" />}
                />
              </Grid>
            </Grid>
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate("/customerCare/list")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Customer"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
      <Snackbar
        open={errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(false)}
      >
        <Alert
          onClose={() => setErrorSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          Failed to update customer. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerUpdate;
